import {Canvas, useFrame, useThree} from '@react-three/fiber'
import {Html, OrbitControls} from '@react-three/drei'
import * as THREE from 'three'
import {useEffect, useRef, useState} from "react";
import "./App.css";
import {CP, initializate, IP, onReady} from "./setting/initParam";
import {getPillar} from "./elements/getPillar";
import {getHarness} from "./elements/getHarness";
import {getF1} from "./elements/getF1";
import {getRafter} from "./elements/getRafter";
import {getF2} from "./elements/getF2";
import {getRidge} from "./elements/getRidge";
import {getF3} from "./elements/getF3";
import {getF4} from "./elements/getF4";
import {getF5} from "./elements/getF5";
import {getF6} from "./elements/getF6";
import {getF7} from "./elements/getF7";
import {getSheathing} from "./elements/getSheathing";
import {MeshStandardMaterial} from "three";
import {
    ControlsLayout,
    createSlotZonesBetweenPillar,
    DimensionLine, FillerPicker, fromRenderStructDTOArray, Ground,
    InitialStruct, LeadForm, LeadFormData, LightV1,
    RenderLayout,
    RenderStruct, Slots, SlotZone, useCameraSettings,
    Vec3, InAppBrowserBanner, Question, DimensionControl,
    FOOT,
    INCH, HALF_FOOT, ModalPage, ColorPicker, hexToDecimal
} from "@3dcalc/3dpClient";
import {api} from "./api/api";
import {getPK} from "./elements/getPK";
import { SpecStructElement, StructData } from "@3dcalc/3dpClient/dist/types/renderElements";
import {locale} from "./setting/locale";

function roundTo(num: number, decimals: number) {
    const factor = 10 ** decimals;
    return Math.round(num * factor) / factor;
}

const DEFAULT_COLORS = [
    '#8c4d24', '#a0522d', '#6b3a2a', '#c8a96e',
    '#d2691e', '#4a3728', '#d4b896', '#8b6914',
    '#2c2c2c', '#f5f5f0'
]

export default function App() {

    const modeBuild = true

    const lang = new URLSearchParams(window.location.search).get('lang') as 'en'|'de'|'ru' || 'en';

    const colorMapRef = useRef<Record<string, string>>({})
    const resizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [isCalculating, setIsCalculating] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState<RenderStruct>()
    const [selectedIndex, setSelectedIndex] = useState<number>(-1)
    const [selectedSource, setSelectedSource] = useState<'main' | string>('main') // string = slotId
    const [currentColor, setCurrentColor] = useState<string>()
    const [activeSlot, setActiveSlot] = useState<Slots | null>(null)

    const [fillerStructs, setFillerStructs] = useState<Record<string, RenderStruct[]>>({});
    const [slots, setSlots] = useState<Slots[]>([]);
    const [palette, setPalette] = useState<string[]>(DEFAULT_COLORS)
    const leadDataRef = useRef<{ name: string; phone: string } | null>(null)

    const modelRef = useRef<THREE.Group | null>(null)
    const [dataRender, setDataRender] = useState<RenderStruct[]>([])
    const [dataSpec, setDataSpec] = useState<SpecStructElement[]>([])
    const [dataSpecFiller, setDataSpecFiller] = useState<Record<string, SpecStructElement[]>>({})

    const [dimensions, setDimensions] = useState({
        height: IP.sizes.h,
        width: IP.sizes.w,
        length: IP.sizes.l,
        angle: IP.angles.a,
        modeBuild: true,
        roofExtension: {
            left: IP.setting.eLeft,
            right: IP.setting.eRight,
            front: IP.setting.eFront,
            back: IP.setting.eBack
        }
    })
    const [params, setParams] = useState<InitialStruct>({
        h: IP.sizes.h,
        l: IP.sizes.l,
        w: IP.sizes.w,
        a: IP.angles.a,
        r: IP.angles.a * Math.PI / 180,
        e: dimensions.roofExtension,
    })
    const cameraSettings = useCameraSettings()

    useEffect(() => {
        setParams({
            h: dimensions.height,
            w: dimensions.width,
            l: dimensions.length,
            a: dimensions.angle,
            r: dimensions.angle * Math.PI / 180,
            e: dimensions.roofExtension,
        })
    }, [dimensions])

    useEffect(() => {
        onReady(() => {
            console.log('готово', IP)
            setParams({
                h: IP.sizes.h,
                l: IP.sizes.l,
                w: IP.sizes.w,
                a: IP.angles.a,
                r: IP.angles.a * Math.PI / 180,
                e: dimensions.roofExtension,
            })
        })
    }, [IP]);

    useEffect(() => {
        onReady(() => {
            console.log('готово', IP)

            if (resizeTimerRef.current) {
                clearTimeout(resizeTimerRef.current);
            }
            setIsCalculating(true);

            resizeTimerRef.current = setTimeout(() => {
                resize();
            }, 2000);

            return () => {
                if (resizeTimerRef.current) {
                    clearTimeout(resizeTimerRef.current);
                }
            };
        })
    }, [params]);

    const resizePillar = async (): Promise<StructData> => {
        const sizePillar = structuredClone(IP.elementData.pillar.sizeBoard)
        sizePillar.l = params.h
        return await getPillar(params, sizePillar, modeBuild);
    }

    const resizeRafter = async (): Promise<StructData> => {
        const sizeRafter = structuredClone(IP.elementData.rafter.sizeBoard)
        return await getRafter(params, sizeRafter, modeBuild);
    }

    const resizeF2 = async (): Promise<StructData> => {
        const sizeF2 = structuredClone(IP.elementData.f2.sizeBoard)
        return await getF2(params, sizeF2, modeBuild);
    }

    const resizeRidge = async (): Promise<StructData> => {
        const sizeRidge = structuredClone(IP.elementData.ridge.sizeBoard)
        sizeRidge.l = params.l
        return await getRidge(params, sizeRidge, modeBuild);
    }

    const resizeF3 = async (): Promise<StructData> => {
        const sizeF3 = structuredClone(IP.elementData.f3.sizeBoard)
        sizeF3.l = 0
        return await getF3(params, sizeF3, modeBuild);
    }

    const resizeF4 = async (): Promise<StructData> => {
        const sizeF4 = structuredClone(IP.elementData.f4.sizeBoard)
        sizeF4.l = 0
        return await getF4(params, sizeF4, modeBuild);
    }

    const resizeF5 = async (): Promise<StructData> => {
        const sizeF5 = structuredClone(IP.elementData.f5.sizeBoard)
        sizeF5.l = CP.maps.f4.point[1] + IP.gash.simple
        return await getF5(params, sizeF5, modeBuild);
    }

    const resizeF6 = async (): Promise<StructData> => {
        const sizeF6 = structuredClone(IP.elementData.f6.sizeBoard)
        sizeF6.l = 0
        return await getF6(params, sizeF6, modeBuild);
    }

    const resizeF7 = async (): Promise<StructData> => {
        const sizeF7 = structuredClone(IP.elementData.f7.sizeBoard)
        sizeF7.l = CP.maps.f6.point[1] + IP.gash.simple
        return await getF7(params, sizeF7, modeBuild);
    }

    const resizeF1 = async (): Promise<StructData> => {
        const sizeF1 = structuredClone(IP.elementData.f1.sizeBoard)
        sizeF1.l = params.w - IP.elementData.pillar.sizeBoard.h
        return await getF1(params, sizeF1, modeBuild);
    }

    const resizeHarness = async (): Promise<StructData> => {
        const sizeHarness = structuredClone(IP.elementData.harness.sizeBoard)
        sizeHarness.l = params.l
        return await getHarness(params, sizeHarness, modeBuild);
    }

    const resizeSheathing = async (): Promise<StructData> => {
        const sizeSheathing = structuredClone(IP.elementData.sheathing.sizeBoard)
        sizeSheathing.l = 0
        return await getSheathing(params, sizeSheathing, modeBuild);
    }

    const resizePK = async (): Promise<StructData> => {
        const sizePK = structuredClone(IP.elementData.pk.sizeBoard)
        sizePK.l = 0
        return await getPK(params, sizePK, modeBuild);
    }

    const applyColors = (dataRender: RenderStruct[]): RenderStruct[] => {
        return dataRender.map(item => {
            const groupName = item.group[0]
            const savedColor = colorMapRef.current[groupName]
            if (!savedColor) return item

            return {
                ...item,
                material: item.material.map(mat => {
                    const m = (mat as THREE.MeshStandardMaterial).clone()
                    m.color.set(savedColor)
                    return m
                }) as THREE.Material[]
            }
        })
    }

    const createSlotZone = () => {
        const _slots = createSlotZonesBetweenPillar({
            id: "wall",
            maps: CP.maps.pillar.lineCentered,
            sizeBoard: {h: IP.elementData.pillar.sizeBoard.h, l: params.h - IP.gash.simple, w: IP.elementData.pillar.sizeBoard.w},
            sizes: {h: params.h, l: params.l, w: params.w}
        })
        setSlots(_slots)
        setFillerStructs({});
    }

    const resize = async () => {
        const _dataRender: RenderStruct[] = []
        const _specData: SpecStructElement[] = []
        initializate()

        let t = await resizePillar()
        _dataRender.push(t.rs)
        _specData.push(...t.spec)
        t = await resizeRafter()
        _dataRender.push(t.rs)
        _specData.push(...t.spec)
        t = await resizeF2()
        _dataRender.push(t.rs)
        _specData.push(...t.spec)
        t = await resizeRidge()
        _dataRender.push(t.rs)
        _specData.push(...t.spec)
        t = await resizeF3()
        _dataRender.push(t.rs)
        _specData.push(...t.spec)

        if (params.w > 4) {
            t = await resizeF4()
            _dataRender.push(t.rs)
            _specData.push(...t.spec)
        }

        if (params.w > 6) {
            t = await resizeF5()
            _dataRender.push(t.rs)
            _specData.push(...t.spec)

            if (params.w > 7) {
                t = await resizeF6()
                _dataRender.push(t.rs)
                _specData.push(...t.spec)
                t = await resizeF7()
                _dataRender.push(t.rs)
                _specData.push(...t.spec)
            }
        }


        t = await resizeF1()
        _dataRender.push(t.rs)
        _specData.push(...t.spec)
        t = await resizeHarness()
        _dataRender.push(t.rs)
        _specData.push(...t.spec)
        t = await resizeSheathing()
        _dataRender.push(t.rs)
        _specData.push(...t.spec)
        t = await resizePK()
        _dataRender.push(t.rs)
        _specData.push(...t.spec)


        const restored = applyColors(_dataRender)

        setDataRender(restored)
        setDataSpec(_specData)
        setIsCalculating(false)


        createSlotZone()
    }

    const updateDimension = (
        field: 'height' | 'width' | 'length' | 'angle',
        value: number
    ) => {
        const rounded = roundTo(value, 4)
        const limits = {
            height: { min: IP.minSize.h, max: IP.maxSize.h },
            width: { min: IP.minSize.w, max: IP.maxSize.w },
            length: { min: IP.minSize.l, max: IP.maxSize.l },
            angle: { min: IP.minSize.a, max: IP.maxSize.a },
        }

        const { min, max } = limits[field]
        const validated = Math.max(min, Math.min(max, rounded))

        setDimensions(prev => ({ ...prev, [field]: validated }))
    }

    const updateRoofExtension = (
        side: 'left' | 'right' | 'front' | 'back',
        delta: number,
        directValue?: number
    ) => {
        const limits = {
            left: { min: IP.minSize.rel, max: IP.maxSize.rel },
            right: { min: IP.minSize.rer, max: IP.maxSize.rer },
            front: { min: IP.minSize.ref, max: IP.maxSize.ref },
            back: { min: IP.minSize.reb, max: IP.maxSize.reb },
        }

        setDimensions(prev => {
            const currentValue = prev.roofExtension[side]
            const newValue = directValue !== undefined
                ? roundTo(directValue, 4)
                : roundTo(currentValue + delta, 4)

            const { min, max } = limits[side]
            const validated = Math.max(min, Math.min(max, newValue))

            return {
                ...prev,
                roofExtension: {
                    ...prev.roofExtension,
                    [side]: validated
                }
            }
        })
    }

    const saveCalculation = async () => {
        setShowForm(true)
        // const res = await api.calculationSave.build({data: dataRender, visibility: "link_only"})
    }


    const questions: Question[] = [
        {
            id: 'service',
            type: 'checkbox',
            label: locale['additional_services'][lang],
            options: [locale['pile_foundation'][lang], locale['delivery'][lang], locale['installation'][lang], locale['gutter_system'][lang]],
            required: false,
        },
        {
            id: 'delivery_address',
            type: 'text',
            label: locale['delivery_address'][lang],
            placeholder: locale['delivery_address'][lang],
            required: false,
        },
    ];

    const handleLeadSubmit = async ({ name, phone, answers }: LeadFormData) => {
        leadDataRef.current = { name, phone }
        const res = await api.calculationSave.build({
            data: [...dataRender, ...Object.values(fillerStructs).flat()],
            visibility: "link_only",
            params: IP,
            spec: [...dataSpec, ...Object.values(dataSpecFiller).flat()],
            leads: { name, phone, ...answers },
        })
        const link = `https://3dp.deallog.pro/viewer/${res.id}`

    }

    const handleFillerSelect = async (slotId: string, fillerId: string) => {
        const slot = slots.find(s => s.id === slotId);
        if (!slot) return;
        const result = await api.fillers.calculate({
            fillerId,
            width: slot.width,
            height: slot.height,
        });
        const renderStruct = fromRenderStructDTOArray(result.props.render.rs);
        const withPosition = renderStruct.map(s => ({
            ...s,
            position: slot.position,
            rotation: slot.rotation,
        }))
        setFillerStructs(prev => ({ ...prev, [slotId]: withPosition }));
        if (result.props.render?.spec && result.props.render?.spec.length > 0) {
            console.log(result.props.render?.spec)
            setDataSpecFiller(prev => ({ ...prev, [slotId]: result.props.render.spec as SpecStructElement[]}))
        }
    };

    const handleFillerClear = (slotId: string) => {
        setFillerStructs(prev => {
            const next = { ...prev }
            delete next[slotId]
            return next
        })
        setDataSpecFiller(prev => {
            const next = { ...prev }
            delete next[slotId]
            return next
        })
    }

    const isMobile = window.innerWidth < 768

    const controls: DimensionControl[] = [
        {
            label: locale['length'][lang],
            value: dimensions.length,
            step: HALF_FOOT,
            onChange: (v) => updateDimension('length', v),
        },
        {
            label: locale['width'][lang],
            value: dimensions.width,
            step: FOOT,
            onChange: (v) => updateDimension('width', v),
        },
        {
            label: locale['height'][lang],
            value: dimensions.height,
            step: FOOT,
            min: 0.3,
            onChange: (v) => updateDimension('height', v),
        },
        {
            label: locale['truss_pitch'][lang],
            value: dimensions.angle,
            isAngle: true,
            step: 1,
            min: 0,
            max: 60,
            onChange: (v) => updateDimension('angle', v),
        },
        {
            label: locale['left_roof_overhang'][lang],
            value: dimensions.roofExtension.left,
            isOverhang: true,
            step: INCH,
            min: 0,
            onChange: (v) => updateRoofExtension('left', 0, v),
        },
        {
            label: locale['right_roof_overhang'][lang],
            value: dimensions.roofExtension.right,
            isOverhang: true,
            step: INCH,
            min: 0,
            onChange: (v) => updateRoofExtension('right', 0, v),
        },
        {
            label: locale['front_roof_overhang'][lang],
            value: dimensions.roofExtension.front,
            isOverhang: true,
            step: INCH,
            min: 0,
            onChange: (v) => updateRoofExtension('front', 0, v),
        },
        {
            label: locale['rear_roof_overhang'][lang],
            value: dimensions.roofExtension.back,
            isOverhang: true,
            step: INCH,
            min: 0,
            onChange: (v) => updateRoofExtension('back', 0, v),
        },
    ]

    return (
        <div className={"page"}>
            {/*<ExportButton objectRef={modelRef} text={"CAD"} options={{zUp: false, keepMaterials: false }} />*/}
            {/*<ExportButton objectRef={modelRef} text={"obj"} options={{zUp: false, keepMaterials: true }} position={[40, 20]}/>*/}
            <InAppBrowserBanner />
            <div className={"param-wrapper"}>
                <ControlsLayout
                    controls={controls}
                    onCalculate={saveCalculation}
                    isMobile={isMobile}
                    title={locale['parameter'][lang]}
                    btnText={locale['calculatePrice'][lang]}
                    units={'ft'}
                />
            </div>
            <div className={"visual-wrapper"} style={{ position: 'relative', width: '100vw', margin: '0 auto' }}>
                {isCalculating && (
                    <div className="calculating-overlay-canvas">
                        <div className="spinner"></div>
                        <div>{locale['calculating_structure'][lang]}</div>
                    </div>
                )}
                <div className="canvas-wrapper" style={{ position: 'relative', width: '100vw' }}>
                    <Canvas
                        key={`${cameraSettings.fov}`}
                        camera={{ position: cameraSettings.position, fov: cameraSettings.fov }}
                        style={{ width: '100vw', height: '100%', margin: '0 auto' }}
                    >
                        <Ground />

                        <DimensionLine
                            from={[params.l / 2, 0, -params.w / 2]}
                            to={[params.l / 2, 0, params.w / 2]}
                            units={'ft'}
                        />
                        <DimensionLine
                            from={[-params.l / 2, 0, params.w / 2]}
                            to={[params.l / 2, 0, params.w / 2]}
                            units={'ft'}
                        />
                        <DimensionLine
                            from={[params.l / 2, params.h - IP.gash.simple, params.w / 2]}
                            to={[params.l / 2, 0, params.w / 2]}
                            units={'ft'}
                        />

                        <LightV1></LightV1>

                        {slots.map(slot => (
                            <SlotZone
                                key={slot.id}
                                id={slot.id}
                                position={slot.position}
                                rotation={slot.rotation}
                                width={slot.width}
                                height={slot.height}
                                onOpen={(id) => setActiveSlot(slots.find(s => s.id === id) ?? null)}
                            />
                        ))}

                        <group ref={modelRef} name={'full'}>
                            <RenderLayout struct={dataRender}
                                          palette={palette}
                                          onPaletteChange={setPalette}
                                          isEditor={true}
                                          onColorChange={(groupIndex, hex) => {
                                              const groupName = dataRender[groupIndex]?.group[0]
                                              if (groupName) colorMapRef.current[groupName] = hex
                                              setDataRender(prev => prev.map((item, idx) => {
                                                  if (idx !== groupIndex) return item
                                                  return {
                                                      ...item,
                                                      material: item.material.map(mat => {
                                                          const m = (mat as MeshStandardMaterial).clone()
                                                          m.color.set(hex)
                                                          return m as THREE.Material
                                                      })
                                                  }
                                              }))
                                          }}
                                          onGroupClick={(groupIndex, groupData) => {
                                              setSelectedIndex(groupIndex)
                                              setSelectedGroup(groupData)
                                              setSelectedSource('main')
                                              setCurrentColor(colorMapRef.current[groupData.group] ?? '#8c4d24')
                                              setModalOpen(true)
                                          }}
                            />
                            {slots.map(slot => {
                                const struct = fillerStructs[slot.id];
                                if (!struct) return null;
                                return (
                                    <RenderLayout
                                        key={slot.id}
                                        struct={struct}
                                        palette={palette}
                                        onPaletteChange={setPalette}
                                        isEditor={true}
                                        onColorChange={(groupIndex, hex) => {
                                            setFillerStructs(prev => ({
                                                ...prev,
                                                [slot.id]: prev[slot.id].map((item, idx) => {
                                                    if (idx !== groupIndex) return item;
                                                    return {
                                                        ...item,
                                                        material: item.material.map(mat => {
                                                            const m = (mat as MeshStandardMaterial).clone();
                                                            m.color.set(hex);
                                                            return m as THREE.Material;
                                                        })
                                                    };
                                                })
                                            }));
                                        }}
                                        onGroupClick={(groupIndex, groupData) => {
                                            console.log(groupIndex, groupData)
                                            setSelectedIndex(groupIndex)
                                            setSelectedGroup(groupData)
                                            console.log(slot.id)
                                            setSelectedSource(slot.id)  // ← запоминаем что это филлер
                                            setCurrentColor(colorMapRef.current[groupData.group] ?? '#8c4d24')
                                            setModalOpen(true)
                                        }}
                                    />
                                );
                            })}
                        </group>

                    <OrbitControls />
                    </Canvas>
                </div>
            </div>


            <ModalPage
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                title={selectedGroup?.group}
                groupDataList={
                    selectedSource === 'main'
                        ? dataRender.filter(r => r.group === selectedGroup?.group)
                        : (fillerStructs[selectedSource] ?? []).filter(r => r.group === selectedGroup?.group)
                }
                spec={
                    selectedSource === 'main'
                        ? dataSpec.filter(s => s.group === selectedGroup?.group)
                        : (dataSpecFiller[selectedSource] ?? []).filter(s => s.group === selectedGroup?.group)
                }
                lang="en"
                units={"ft"}
            >
                <ColorPicker
                    value={currentColor}
                    onChange={hex => {
                        setCurrentColor(hex)
                        if (selectedIndex === null) return

                        if (selectedSource === 'main') {
                            // основная конструкция
                            if (selectedGroup?.group && IP.elementData[selectedGroup.group.toLowerCase()]?.color) {
                                IP.elementData[selectedGroup.group.toLowerCase()].color = hexToDecimal(hex)
                            }
                            setDataRender(prev => prev.map((item, idx) => {
                                if (idx !== selectedIndex) return item
                                const updated = {
                                    ...item,
                                    material: item.material.map(mat => {
                                        const m = (mat as MeshStandardMaterial).clone()
                                        m.color.set(hex)
                                        return m as THREE.Material
                                    })
                                }
                                setSelectedGroup(updated)
                                return updated
                            }))
                        } else {
                            // филлер — selectedSource это slotId
                            setFillerStructs(prev => ({
                                ...prev,
                                [selectedSource]: prev[selectedSource].map((item, idx) => {
                                    if (idx !== selectedIndex) return item
                                    const updated = {
                                        ...item,
                                        material: item.material.map(mat => {
                                            const m = (mat as MeshStandardMaterial).clone()
                                            m.color.set(hex)
                                            return m as THREE.Material
                                        })
                                    }
                                    setSelectedGroup(updated)
                                    return updated
                                })
                            }))
                        }
                    }}

                    palette={palette}
                    onPaletteChange={setPalette}
                    lang="en"
                />
                {/*{selectedGroup?.group && groupSettingsFilter.includes(selectedGroup?.group.toLowerCase())?*/}
                {/*    <DecorativeJoinPicker*/}
                {/*        settings={dJSetting(IP.elementData[selectedGroup?.group.toLowerCase()])}*/}
                {/*        activeKey={groupSettings[selectedGroup?.group ?? '']?.joinKey}*/}
                {/*        onChange={(key, config) => handleJoinChange(selectedGroup?.group ?? '', key, config)}*/}
                {/*    />*/}
                {/*    :null}*/}

                {/*<MaterialPicker*/}
                {/*    fetchMaterials={() => api.getMaterials.fetch(CP.materials[selectedGroup?.group?.toLowerCase() ?? ''])}*/}
                {/*    activeId={groupSettings[selectedGroup?.group ?? '']?.materialId}*/}
                {/*    onChange={(id, material) => handleMaterialChange(selectedGroup?.group ?? '', id, material)}*/}
                {/*    units="ft"*/}
                {/*/>*/}
            </ModalPage>


            {activeSlot && (
                <div style={{
                    position: 'fixed', left: 0, top: 0, bottom: 0,
                    width: window.innerWidth < 768 ? '100vw' : '50vw',
                    background: 'rgba(0,0,0,0.58)', zIndex: 1000,
                    boxShadow: '8px 0 32px rgba(0,0,0,0.15)',
                    overflowY: 'auto', padding: 24,
                }}>
                    <FillerPicker
                        width={activeSlot.width}
                        height={activeSlot.height}
                        depth={activeSlot.depth}
                        slotType={activeSlot.type ?? 'wall'}
                        fetchAvailable={api.fillers.available}
                        onSelect={(fillerId) => {
                            handleFillerSelect(activeSlot.id, fillerId)
                            setActiveSlot(null)
                        }}
                        onClose={() => setActiveSlot(null)}
                        units={'ft'}
                        lang={'en'}
                        filled={!!activeSlot.id}
                        onClear={() => {
                            handleFillerClear(activeSlot.id)
                            setActiveSlot(null)
                        }}
                    />
                </div>
            )}

            {showForm && (
                <LeadForm
                    onSubmit={handleLeadSubmit}
                    onClose={() => setShowForm(false)}
                    title={locale['save_configuration'][lang]}
                    subtitle={locale['leave_contacts_for_quote'][lang]}
                    questions={questions}
                    lang={lang}
                    country={'us'}
                />
            )}
        </div>
    )
}

function CameraDebug() {
    const { camera } = useThree()
    const paramCamera: {
        pos: Vec3,
        fov: number
    } = {
        pos: [0, 0, 0],
        fov: 0
    }
    const [state, setState] = useState(paramCamera)

    useFrame(() => {
        if (camera instanceof THREE.PerspectiveCamera) {
            setState({
                pos: [
                    Number(camera.position.x.toFixed(2)),
                    Number(camera.position.y.toFixed(2)),
                    Number(camera.position.z.toFixed(2))
                ],
                fov: Number(camera.fov.toFixed(2))
            })
        }
    })

    return (
        <Html position={[0, 0, 0]} style={{ color: 'white', fontSize: 14, width: '250px', marginLeft: '-460px', marginTop: '180px' }}>
            <div>
                <div>pos: [{state.pos.join(', ')}]</div>
                <div>fov: {state.fov}</div>
            </div>
        </Html>
    )
}
